import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Filter,
  Calendar,
  MapPin,
  X
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { EntityEditForm } from '@/components/EntityEditForm'
import { ConfirmationModal } from '@/components/monitoring/ConfirmationModal'
import { useAuth } from '@/hooks/useAuth'

// Types
interface DeveloperFilters {
  search: string
  page: number
  limit: number
  sortBy: string
  sortOrder: 'asc' | 'desc'
  listing_date_from: string
  listing_date_to: string
  city: string
  district: string
}

export function Developers() {
  const [filters, setFilters] = useState<DeveloperFilters>({
    search: '',
    page: 1,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'desc',
    listing_date_from: '',
    listing_date_to: '',
    city: '',
    district: '',
  })
  const [selectedDevelopers, setSelectedDevelopers] = useState<number[]>([])
  const [showFilters, setShowFilters] = useState(false)

  const [editingDeveloper, setEditingDeveloper] = useState<number | null>(null)
  const [creatingDeveloper, setCreatingDeveloper] = useState(false)
  const [deletingDeveloper, setDeletingDeveloper] = useState<
    { id: number; name: string } | null
  >(null)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canMutate } = useAuth()
  const rowColSpan = canMutate ? 9 : 8

  // Sortable header component
  const SortableHeader = ({ column, children, className = "" }: { column: string; children: React.ReactNode; className?: string }) => {
    const isActive = filters.sortBy === column
    const isAsc = isActive && filters.sortOrder === 'asc'
    const isDesc = isActive && filters.sortOrder === 'desc'

    return (
      <th 
        className={`px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          <div className="flex flex-col">
            <ChevronUp 
              className={`h-3 w-3 ${isAsc ? 'text-primary-600' : 'text-secondary-400'}`} 
            />
            <ChevronDown 
              className={`h-3 w-3 -mt-1 ${isDesc ? 'text-primary-600' : 'text-secondary-400'}`} 
            />
          </div>
        </div>
      </th>
    )
  }

  // Fetch developers with filtering and pagination
  const { data: developersResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['developers', filters],
    queryFn: async () => {
      try {
        console.log('Fetching developers with filters:', filters);
        const params = new URLSearchParams()
        params.append('page', filters.page.toString())
        params.append('limit', filters.limit.toString())
        if (filters.search) params.append('search', filters.search)
        if (filters.sortBy) params.append('sortBy', filters.sortBy)
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)
        if (filters.listing_date_from) params.append('listing_date_from', filters.listing_date_from)
        if (filters.listing_date_to) params.append('listing_date_to', filters.listing_date_to)
        if (filters.city) params.append('city', filters.city)
        if (filters.district) params.append('district', filters.district)

        const response = await api.get(`/developers?${params}`)
        console.log('Developers response:', response.data);

        return response.data
      } catch (error) {
        console.error('Error fetching developers:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
  })

  // Fetch distinct cities for filter dropdown
  const { data: citiesData } = useQuery({
    queryKey: ['developer-filter-cities'],
    queryFn: async () => {
      const response = await api.get('/developers/filter/cities')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

  // Fetch distinct districts for filter dropdown
  const { data: districtsData } = useQuery({
    queryKey: ['developer-filter-districts'],
    queryFn: async () => {
      const response = await api.get('/developers/filter/districts')
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const cities: string[] = citiesData?.data || []
  const districts: string[] = districtsData?.data || []

  const developers = developersResponse?.data || []
  const totalDevelopers = developersResponse?.meta?.total || 0
  const totalPages = Math.ceil(totalDevelopers / filters.limit)

  // Process developers data
  const processedDevelopers = useMemo(() => {
    return developers.map((developer: any) => ({
      ...developer,
      name: developer.name || 'Unnamed Developer',
      nr_projects: Number(developer?.nr_projects ?? 0),
      nr_units: Number(developer?.nr_units ?? 0),
      total_m2: Number(developer?.total_m2 ?? 0),
      nr_units_available: Number(developer?.nr_units_available ?? 0),
      total_m2_available: Number(developer?.total_m2_available ?? 0),
    }))
  }, [developers])

  // Bulk delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (developerIds: number[]) => {
      const deletePromises = developerIds.map(id => api.delete(`/developers/${id}`))
      await Promise.all(deletePromises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developers'] })
      setSelectedDevelopers([])
      toast.success('Developers deleted successfully')
    },
    onError: (error) => {
      console.error('Error deleting developers:', error)
      toast.error('Failed to delete developers')
    }
  })

  // Single delete mutation
  const singleDeleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/developers/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developers'] })
      queryClient.invalidateQueries({ queryKey: ['/developers-dropdown'] })
      toast.success('Developer deleted successfully')
      setDeletingDeveloper(null)
    },
    onError: (error: any) => {
      console.error('Error deleting developer:', error)
      const status = error?.response?.status
      const backendMessage = error?.response?.data?.message
      if (status === 409 || /foreign key|constraint/i.test(String(backendMessage))) {
        toast.error('Cannot delete: this developer still has associated projects. Reassign them first.')
      } else {
        toast.error(
          Array.isArray(backendMessage) ? backendMessage[0] : backendMessage || 'Failed to delete developer'
        )
      }
    }
  })

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }))
  }

  // Handle sorting
  const handleSort = (column: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1
    }))
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedDevelopers.length === 0) {
      toast.error('Please select developers to delete')
      return
    }

    if (window.confirm(`Are you sure you want to delete ${selectedDevelopers.length} developers?`)) {
      deleteMutation.mutate(selectedDevelopers)
    }
  }

  // Handle single delete — opens confirmation modal after pre-validating FK usage
  const handleDeleteDeveloper = (
    developer: { developer_id: number; name: string; nr_projects: number }
  ) => {
    if (developer.nr_projects > 0) {
      toast.error(
        `Cannot delete "${developer.name}": it has ${developer.nr_projects} associated project${
          developer.nr_projects === 1 ? '' : 's'
        }. Reassign or delete those projects first.`
      )
      return
    }
    setDeletingDeveloper({ id: developer.developer_id, name: developer.name })
  }

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDevelopers(processedDevelopers.map(developer => developer.developer_id))
    } else {
      setSelectedDevelopers([])
    }
  }

  // Handle select individual
  const handleSelectDeveloper = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedDevelopers(prev => [...prev, id])
    } else {
      setSelectedDevelopers(prev => prev.filter(developerId => developerId !== id))
    }
  }

  const handleRowClick = (developerId: number) => {
    navigate(`/developers/${developerId}`)
  }

  // Time period preset helper
  const applyTimePeriodPreset = (months: number) => {
    const to = new Date()
    const from = new Date()
    from.setMonth(from.getMonth() - months)
    setFilters(prev => ({
      ...prev,
      listing_date_from: from.toISOString().split('T')[0],
      listing_date_to: to.toISOString().split('T')[0],
      page: 1,
    }))
  }

  const clearTimePeriod = () => {
    setFilters(prev => ({ ...prev, listing_date_from: '', listing_date_to: '', page: 1 }))
  }

  const clearLocationFilters = () => {
    setFilters(prev => ({ ...prev, city: '', district: '', page: 1 }))
  }

  const clearAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      listing_date_from: '',
      listing_date_to: '',
      city: '',
      district: '',
      page: 1,
    }))
  }

  // Count active filters for badge
  const activeFilterCount = [
    filters.listing_date_from || filters.listing_date_to,
    filters.city,
    filters.district,
  ].filter(Boolean).length

  // Determine active time preset for button highlighting
  const getActivePreset = (): number | null => {
    if (!filters.listing_date_from || !filters.listing_date_to) return null
    const from = new Date(filters.listing_date_from)
    const to = new Date(filters.listing_date_to)
    const today = new Date()
    if (Math.abs(to.getTime() - today.getTime()) > 86400000 * 2) return null
    const monthsDiff = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())
    if ([6, 12, 24, 36].includes(monthsDiff)) return monthsDiff
    return null
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-danger-600 mb-2">Error loading developers</div>
          <button
            onClick={() => refetch()}
            className="btn-primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="bg-white rounded-xl shadow-tagus p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search developers..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCreatingDeveloper(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Developer
            </button>
            {selectedDevelopers.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deleteMutation.isPending}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedDevelopers.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
            activeFilterCount > 0
              ? 'border-primary-300 bg-primary-50 text-primary-700'
              : 'border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50'
          }`}
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-primary-600 text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-secondary-500 hover:text-secondary-700 flex items-center space-x-1"
          >
            <X className="h-3 w-3" />
            <span>Clear all filters</span>
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-tagus p-6 space-y-6">
          {/* Time Period Section */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Calendar className="h-4 w-4 text-secondary-500" />
              <h3 className="text-sm font-medium text-secondary-900">Listing Date Period</h3>
              {(filters.listing_date_from || filters.listing_date_to) && (
                <button onClick={clearTimePeriod} className="text-xs text-secondary-400 hover:text-secondary-600 ml-auto">
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {[
                { label: '6 Months', months: 6 },
                { label: '1 Year', months: 12 },
                { label: '2 Years', months: 24 },
                { label: '3 Years', months: 36 },
              ].map(({ label, months }) => (
                <button
                  key={months}
                  onClick={() => applyTimePeriodPreset(months)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    getActivePreset() === months
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-secondary-300 text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  {label}
                </button>
              ))}
              <span className="text-secondary-400 text-sm">or</span>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  className="form-input text-sm py-1.5"
                  value={filters.listing_date_from}
                  onChange={(e) => setFilters(prev => ({ ...prev, listing_date_from: e.target.value, page: 1 }))}
                />
                <span className="text-secondary-400 text-sm">to</span>
                <input
                  type="date"
                  className="form-input text-sm py-1.5"
                  value={filters.listing_date_to}
                  onChange={(e) => setFilters(prev => ({ ...prev, listing_date_to: e.target.value, page: 1 }))}
                />
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <MapPin className="h-4 w-4 text-secondary-500" />
              <h3 className="text-sm font-medium text-secondary-900">Location</h3>
              {(filters.city || filters.district) && (
                <button onClick={clearLocationFilters} className="text-xs text-secondary-400 hover:text-secondary-600 ml-auto">
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1">City</label>
                <select
                  className="form-input text-sm"
                  value={filters.city}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value, page: 1 }))}
                >
                  <option value="">All Cities</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1">District</label>
                <select
                  className="form-input text-sm"
                  value={filters.district}
                  onChange={(e) => setFilters(prev => ({ ...prev, district: e.target.value, page: 1 }))}
                >
                  <option value="">All Districts</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active filters indicator */}
      {activeFilterCount > 0 && (
        <div className="text-xs text-secondary-500 flex items-center flex-wrap gap-2">
          <span>Showing metrics for projects</span>
          {(filters.listing_date_from || filters.listing_date_to) && (
            <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded">
              {filters.listing_date_from && filters.listing_date_to
                ? `${filters.listing_date_from} to ${filters.listing_date_to}`
                : filters.listing_date_from
                  ? `from ${filters.listing_date_from}`
                  : `until ${filters.listing_date_to}`}
            </span>
          )}
          {filters.city && (
            <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded">{filters.city}</span>
          )}
          {filters.district && (
            <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded">{filters.district}</span>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-tagus overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-secondary-200 table-fixed">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="w-16 px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedDevelopers.length === processedDevelopers.length && processedDevelopers.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <SortableHeader column="name" className="text-left">
                  Name
                </SortableHeader>
                <SortableHeader column="nr_projects" className="w-36 text-right">
                  Number Projects
                </SortableHeader>
                <SortableHeader column="nr_units" className="w-32 text-right">
                  Number Units
                </SortableHeader>
                <SortableHeader column="total_m2" className="w-32 text-right">
                  Total M2
                </SortableHeader>
                <SortableHeader column="nr_units_available" className="w-36 text-right">
                  Units Available
                </SortableHeader>
                <SortableHeader column="total_m2_available" className="w-32 text-right">
                  M2 Available
                </SortableHeader>
                <SortableHeader column="created_at" className="w-32 text-left">
                  Created
                </SortableHeader>
                {canMutate && (
                  <th className="w-24 px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {isLoading ? (
                <tr>
                  <td colSpan={rowColSpan} className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-5 w-5 animate-spin text-primary-600 mr-2" />
                      Loading developers...
                    </div>
                  </td>
                </tr>
              ) : processedDevelopers.length === 0 ? (
                <tr>
                  <td colSpan={rowColSpan} className="px-6 py-4 text-center text-secondary-500">
                    No developers found
                  </td>
                </tr>
              ) : (
                processedDevelopers.map((developer) => (
                <tr
                  key={developer.developer_id}
                  className="hover:bg-secondary-50 cursor-pointer"
                  onClick={() => handleRowClick(developer.developer_id)}
                >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedDevelopers.includes(developer.developer_id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(e) => handleSelectDeveloper(developer.developer_id, e.target.checked)}
                        className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 truncate max-w-[200px] sm:max-w-xs md:max-w-sm lg:max-w-md xl:max-w-lg">
                      <div className="text-sm font-medium text-secondary-900 truncate" title={developer.name}>
                        {developer.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {developer.nr_projects.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {developer.nr_units.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {developer.total_m2.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {developer.nr_units_available.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {developer.total_m2_available.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {new Date(developer.created_at).toLocaleDateString()}
                    </td>
                    {canMutate && (
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDeleteDeveloper(developer)
                          }}
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-danger-600 hover:bg-danger-50 hover:text-danger-700 focus:outline-none focus:ring-2 focus:ring-danger-500"
                          title={`Delete ${developer.name}`}
                          aria-label={`Delete ${developer.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-700">
            Showing {((filters.page - 1) * filters.limit) + 1} to {Math.min(filters.page * filters.limit, totalDevelopers)} of {totalDevelopers} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-secondary-700">
              Page {filters.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page === totalPages}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(creatingDeveloper || editingDeveloper) && (
        <EntityEditForm
          type="developer"
          entityId={editingDeveloper}
          onClose={() => {
            setCreatingDeveloper(false)
            setEditingDeveloper(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['developers'] })
            setCreatingDeveloper(false)
            setEditingDeveloper(null)
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deletingDeveloper !== null}
        onClose={() => setDeletingDeveloper(null)}
        onConfirm={() => {
          if (deletingDeveloper) singleDeleteMutation.mutate(deletingDeveloper.id)
        }}
        title="Delete Developer"
        message={
          deletingDeveloper
            ? `Are you sure you want to permanently delete "${deletingDeveloper.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={singleDeleteMutation.isPending}
      />
    </div>
  )
}
